import React from 'react';
import { SimpleReveal } from './SimpleReveal';
import spotifyStreamsVideo from '@/assets/spotify-streams-new.mp4';
import { trackLead } from '@/lib/metaPixel';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';

interface BeforeAfterCTAProps {
  applyUrl: string;
}
export function BeforeAfterCTA({
  applyUrl
}: BeforeAfterCTAProps) {
  return <section id="how-it-works" className="relative overflow-hidden py-20 lg:py-28" style={{
    background: 'radial-gradient(ellipse at center, hsl(0, 0%, 7%) 0%, hsl(0, 0%, 3%) 70%)'
  }}>
        {/* Static noise particles - visible at edges */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at center, rgba(255,255,255,0.12) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            opacity: 0.08,
            maskImage: 'linear-gradient(to right, black 0%, black 12%, transparent 30%, transparent 70%, black 88%, black 100%)',
            WebkitMaskImage: 'linear-gradient(to right, black 0%, black 12%, transparent 30%, transparent 70%, black 88%, black 100%)'
          }}
        />
        <div className="absolute inset-0 bg-[#0071e3]/[0.01]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#0071e3]/30 to-transparent" />
      <div className="relative z-10 max-w-3xl mx-auto px-6">
        {/* Section Label */}
        <SimpleReveal>
          <div className="text-center mb-4 lg:mb-6">
            <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md shadow-[0_0_20px_rgba(0,113,227,0.3)] md:shadow-none transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
              <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
                &gt; Next Steps
              </span>
            </div>
          </div>
        </SimpleReveal>

        {/* Headline */}
        <SimpleReveal delay={80}>
          <h2 className="headline-xl text-white text-center mb-3 lg:mb-3">
            From invisible to{' '}
            <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(30,215,96,0.7)]">
              unstoppable
            </span>
          </h2>
        </SimpleReveal>

        {/* Subheader */}
        <SimpleReveal delay={100}>
          <p className="body-large text-white/70 text-center max-w-xl mx-auto mb-16 lg:mb-20">
            A proven system for artists ready to scale.
          </p>
        </SimpleReveal>

        {/* Video */}
        <SimpleReveal delay={160}>
          <div className="mb-16 max-w-2xl mx-auto">
            {/* Outer container with glow effect */}
            <div className="relative">
              {/* Subtle ambient glow */}
              <div className="absolute inset-0 blur-xl opacity-25" style={{
              background: 'radial-gradient(ellipse at center, rgba(0, 113, 227, 0.3) 0%, transparent 70%)'
            }} />
              
              {/* Video container with mask and inner shadow */}
              <div className="relative overflow-hidden transition-all duration-500" style={{
              maskImage: 'linear-gradient(to right, rgba(0,0,0,0) 0%, black 15%, black 85%, rgba(0,0,0,0) 100%)',
              WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,0) 0%, black 15%, black 85%, rgba(0,0,0,0) 100%)',
              boxShadow: 'inset 0 0 40px 10px rgba(0, 0, 0, 0.5), inset 0 0 80px 20px rgba(0, 0, 0, 0.2)'
            }}>
                <video autoPlay loop muted playsInline className="w-full h-auto" style={{
                transform: 'translateZ(0)',
                willChange: 'transform'
              }}>
                  <source src={spotifyStreamsVideo} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                
                {/* Dark vignette overlay on the video itself */}
                <div className="absolute inset-0 pointer-events-none" style={{
                background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0, 0, 0, 0.4) 70%, rgba(0, 0, 0, 0.8) 90%)'
              }} />
                
                {/* Subtle noise overlay on video - creates visual continuity */}
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: 'radial-gradient(circle at center, rgba(255,255,255,0.08) 1px, transparent 1px)',
                    backgroundSize: '20px 20px',
                    opacity: 0.06,
                  }}
                />
              </div>
            </div>
            <p className="text-sm text-white/60 text-center mt-3">
              Real artist growth over time on a single release with us
            </p>
          </div>
        </SimpleReveal>

        {/* Body Text */}
        <SimpleReveal delay={240}>
          <div className="text-center max-w-xl mx-auto mb-10">
            <p className="body-large text-white/70 max-w-[600px] mx-auto mb-4 leading-relaxed">
              We are application only because we fully prioritize your growth and only take on artists we believe we can scale.  When you work with us, you're not hiring an agency: you're building your team.      
            </p>
            
          </div>
        </SimpleReveal>

        {/* CTA Button */}
        <SimpleReveal delay={280}>
          <div className="text-center">
            <div className="inline-block group relative rounded-xl rotating-border-button">
              <Button variant="hero-blue-ring" size="hero" asChild>
                <a href={applyUrl} target="_blank" rel="noopener noreferrer" onClick={trackLead}>
                  <span className="inline-flex items-center gap-2">
                    Apply Now
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </a>
              </Button>
            </div>
            
            <p className="text-xs sm:text-sm font-normal text-white/60 mt-4">
              We review all applications personally
            </p>
          </div>
        </SimpleReveal>
        </div>
      </section>;
}
export default BeforeAfterCTA;